import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';

const PasswordChangeModal = ({ open, onClose, onPasswordChanged }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [updating, setUpdating] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (formData.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (formData.currentPassword === formData.newPassword) {
      toast.error('New password must be different from your current password');
      return;
    }

    setUpdating(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('admin_token');

      const response = await fetch(`${BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: formData.currentPassword,
          new_password: formData.newPassword
        })
      });

      // Safely parse body whether JSON or plain text
      let data = null;
      try {
        data = await response.json();
      } catch (_) {
        data = null;
      }

      if (!response.ok) {
        const errorMessage = parseApiError(data, response.status);
        toast.error(errorMessage);
        return;
      }

      localStorage.setItem('password_changed', 'true');
      toast.success(data?.message || 'Password updated successfully');
      onPasswordChanged();
      onClose();
    } catch (error) {
      console.error('Change password network error:', error);
      toast.error('Network error — please check your connection and try again.');
    } finally {
      setUpdating(false);
    }
  };

  // Convert any FastAPI error body into a human-readable string. Handles:
  //   - { detail: "string" }
  //   - { detail: [{ loc: [...], msg: "...", type: "..." }, ...] }  (Pydantic 422)
  //   - { detail: { msg: "..." } }
  //   - plain string / null
  const parseApiError = (data, status) => {
    if (!data) return `Request failed (HTTP ${status})`;
    const detail = data.detail;

    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail
        .map((d) => {
          if (typeof d === 'string') return d;
          const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== 'body').join('.') : '';
          const msg = d?.msg || 'Invalid value';
          return field ? `${field}: ${msg}` : msg;
        })
        .join(' \u2022 ');
    }
    if (detail && typeof detail === 'object') return detail.msg || JSON.stringify(detail);
    if (typeof data.message === 'string') return data.message;
    return `Request failed (HTTP ${status})`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            <DialogTitle className="text-xl">Change Default Password</DialogTitle>
          </div>
          <DialogDescription className="text-gray-400">
            For security reasons, please change your default password immediately.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Current Password
            </label>
            <Input
              type="password"
              required
              value={formData.currentPassword}
              onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
              className="bg-gray-800 border-gray-700 text-white"
              placeholder="Enter current password"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              New Password
            </label>
            <Input
              type="password"
              required
              value={formData.newPassword}
              onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
              className="bg-gray-800 border-gray-700 text-white"
              placeholder="Enter new password (min 8 characters)"
              minLength={8}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Confirm New Password
            </label>
            <Input
              type="password"
              required
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              className="bg-gray-800 border-gray-700 text-white"
              placeholder="Confirm new password"
              minLength={8}
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              disabled={updating}
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
            >
              {updating ? 'Updating...' : 'Change Password'}
            </Button>
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="border-gray-700 text-gray-300 hover:bg-gray-800"
            >
              Skip for Now
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PasswordChangeModal;
